/**
 * Presents archived email metadata in a read-only, remotely processed
 * DevExtreme grid and delegates body loading to an isolated master-detail
 * component.
 */
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import DataGrid, {
	Column,
	ColumnHeaderFilter,
	FilterRow,
	HeaderFilter,
	Item,
	LoadPanel,
	MasterDetail,
	Pager,
	Paging,
	Scrolling,
	Selection,
	Sorting,
	Toolbar,
} from "devextreme-react/data-grid";
import { Snackbar } from "../../components";
import {
	ARCHIVED_EMAIL_ERROR_MESSAGES,
	isArchivedEmailAuthorizationError,
} from "../../api/email/archivedEmailsApi";
import createArchivedEmailsDataSource, {
	UNSUPPORTED_ARCHIVED_EMAIL_FILTER_CODE,
	UNSUPPORTED_ARCHIVED_EMAIL_FILTER_MESSAGE,
} from "./createArchivedEmailsDataSource";
import ArchivedEmailDetail from "./ArchivedEmailDetail";
import "./archived-emails.scss";

const PAGE_SIZES = [25, 50, 100];
const TEXT_FILTER_OPERATIONS = ["contains"];
const DATE_FILTER_OPERATIONS = ["=", ">=", "<"];

/**
 * Keeps the archived-email results within a predictable viewport while
 * allowing the grid to manage row and expanded-detail scrolling.
 */
const ARCHIVED_EMAIL_GRID_HEIGHT = 600;

/**
 * Creates the grid source and the four bounded remote header-filter sources.
 * Keeping them together guarantees their stores remain stable across renders.
 *
 * @returns {object} Page-lifetime grid, header, and detail resources.
 */
const createPageResources = () => {
	const resources = createArchivedEmailsDataSource();
	return {
		...resources,
		headerSources: {
			caseNumber:
				resources.createHeaderFilterDataSource("caseNumber"),
			sender: resources.createHeaderFilterDataSource("sender"),
			recipient:
				resources.createHeaderFilterDataSource("recipient"),
			subject: resources.createHeaderFilterDataSource("subject"),
		},
	};
};

/**
 * Renders a stable empty string and supplies full text as a native tooltip.
 *
 * @param {object} cell DevExtreme cell template information.
 * @returns {JSX.Element} Wrapped inert cell text.
 */
export const renderArchivedEmailTextCell = (cell) => {
	const text = cell?.value ?? "";
	return (
		<span className="archived-email-cell-text" title={text}>
			{text}
		</span>
	);
};

/**
 * Truncates recipient text visually while preserving the complete value in
 * the native hover tooltip.
 *
 * @param {object} cell DevExtreme cell template information.
 * @returns {JSX.Element} Recipient text with its complete tooltip value.
 */
export const renderArchivedEmailRecipientCell = (cell) => {
	const text = cell?.value ?? "";
	return (
		<span
			className="archived-email-cell-text archived-email-recipient-cell"
			title={text}
		>
			{text}
		</span>
	);
};

/**
 * Expands or collapses the focused row with Enter or Space. Editor controls
 * are excluded so filtering and header-filter keyboard input retain their
 * native behavior.
 *
 * @param {object} event DevExtreme key-down event.
 * @returns {boolean} Whether a master-detail row was toggled.
 */
export const toggleArchivedEmailDetailFromKeyboard = (event) => {
	const nativeEvent = event?.event;
	if (
		nativeEvent?.key !== "Enter" &&
		nativeEvent?.key !== " " &&
		nativeEvent?.key !== "Spacebar"
	) {
		return false;
	}

	if (
		nativeEvent.target?.closest?.(
			"input, textarea, select, button, [contenteditable='true']"
		)
	) {
		return false;
	}

	const component = event?.component;
	const focusedRowKey = component?.option?.("focusedRowKey");
	if (
		focusedRowKey === undefined ||
		focusedRowKey === null ||
		!component?.isRowExpanded
	) {
		return false;
	}

	nativeEvent.preventDefault?.();
	if (component.isRowExpanded(focusedRowKey)) {
		component.collapseRow(focusedRowKey);
	} else {
		component.expandRow(focusedRowKey);
	}
	return true;
};

/**
 * Renders the read-only Archived Emails grid.
 *
 * @returns {JSX.Element} Archived Emails page.
 */
const ArchivedEmailsPage = () => {
	const resources = useMemo(createPageResources, []);
	const archivedEmailsGridRef = useRef(null);
	const [snackbarOptions, setSnackbarOptions] = useState({
		visible: false,
		type: "error",
		message: "",
	});

	useEffect(
		() => () => {
			resources.clearDetailCache();
		},
		[resources]
	);

	/**
	 * Shows only allowlisted list failures. Authorization responses remain
	 * available to the existing route/authentication handling.
	 *
	 * @param {object} event DevExtreme data-error event.
	 */
	const handleDataError = useCallback((event) => {
		const error = event?.error;
		if (isArchivedEmailAuthorizationError(error)) return;

		const message =
			error?.code === UNSUPPORTED_ARCHIVED_EMAIL_FILTER_CODE
				? UNSUPPORTED_ARCHIVED_EMAIL_FILTER_MESSAGE
				: ARCHIVED_EMAIL_ERROR_MESSAGES.list;
		setSnackbarOptions({
			visible: true,
			type: "error",
			message,
		});
	}, []);

	/**
	 * Supplies one lazy detail component for the expanded grid row.
	 *
	 * @param {object} detailInfo DevExtreme master-detail information.
	 * @returns {JSX.Element} Lazy body component.
	 */
	const renderDetail = useCallback(
		(detailInfo) => (
			<ArchivedEmailDetail
				email={detailInfo?.data}
				loadDetailAsync={resources.loadDetailAsync}
			/>
		),
		[resources.loadDetailAsync]
	);

	/**
	 * Reloads the current grid view while retaining its paging, sorting, and
	 * filtering state. Clearing cached bodies ensures later expansions also
	 * retrieve current archived-email detail.
	 */
	const handleRefresh = useCallback(() => {
		resources.clearDetailCache();
		archivedEmailsGridRef.current?.instance?.refresh();
	}, [resources]);

	const refreshButtonOptions = useMemo(
		() => ({
			icon: "refresh",
			text: "Refresh",
			hint: "Refresh archived emails",
			stylingMode: "text",
			elementAttr: {
				"aria-label": "Refresh archived emails",
			},
			onClick: handleRefresh,
		}),
		[handleRefresh]
	);

	return (
		<div className="archived-emails-page">
			<h2 className="content-block">Archived Emails</h2>

			<div className="dx-card wide-card archived-emails-card">
				<DataGrid
					ref={archivedEmailsGridRef}
					dataSource={resources.dataSource}
					keyExpr="id"
					height={ARCHIVED_EMAIL_GRID_HEIGHT}
					showBorders={true}
					wordWrapEnabled={true}
					columnAutoWidth={true}
					columnHidingEnabled={true}
					focusStateEnabled={true}
					focusedRowEnabled={true}
					errorRowEnabled={false}
					noDataText="No archived emails were found."
					remoteOperations={{
						paging: true,
						sorting: true,
						filtering: true,
					}}
					onDataErrorOccurred={handleDataError}
					onKeyDown={toggleArchivedEmailDetailFromKeyboard}
				>
					<Toolbar>
						<Item
							location="after"
							widget="dxButton"
							options={refreshButtonOptions}
						/>
					</Toolbar>
					<Paging defaultPageSize={25} />
					<Scrolling
						mode="standard"
						useNative={true}
						showScrollbar="always"
					/>
					<Pager
						allowedPageSizes={PAGE_SIZES}
						showPageSizeSelector={true}
						showNavigationButtons={true}
						showInfo={true}
					/>
					<Sorting mode="single" />
					<Selection mode="none" />
					<FilterRow visible={true} />
					<HeaderFilter visible={true} />
					<LoadPanel
						enabled={true}
						showIndicator={true}
						showPane={true}
					/>
					<MasterDetail
						enabled={true}
						autoExpandAll={false}
						render={renderDetail}
					/>

					<Column
						dataField="caseNumber"
						caption="Case Number"
						filterOperations={TEXT_FILTER_OPERATIONS}
						hidingPriority={3}
						cellRender={renderArchivedEmailTextCell}
					>
						<ColumnHeaderFilter
							dataSource={
								resources.headerSources.caseNumber
							}
							allowSelectAll={false}
							search={{ enabled: true }}
						/>
					</Column>
					<Column
						dataField="sender"
						caption="Sender"
						filterOperations={TEXT_FILTER_OPERATIONS}
						hidingPriority={1}
						cellRender={renderArchivedEmailTextCell}
					>
						<ColumnHeaderFilter
							dataSource={resources.headerSources.sender}
							allowSelectAll={false}
							search={{ enabled: true }}
						/>
					</Column>
					<Column
						dataField="recipient"
						caption="Recipient"
						width={200}
						filterOperations={TEXT_FILTER_OPERATIONS}
						hidingPriority={0}
						cellRender={renderArchivedEmailRecipientCell}
					>
						<ColumnHeaderFilter
							dataSource={
								resources.headerSources.recipient
							}
							allowSelectAll={false}
							search={{ enabled: true }}
						/>
					</Column>
					<Column
						dataField="subject"
						caption="Subject"
						minWidth={240}
						filterOperations={TEXT_FILTER_OPERATIONS}
						hidingPriority={4}
						cellRender={renderArchivedEmailTextCell}
					>
						<ColumnHeaderFilter
							dataSource={resources.headerSources.subject}
							allowSelectAll={false}
							search={{ enabled: true }}
						/>
					</Column>
					<Column
						dataField="sentDateTime"
						caption="Date Sent"
						dataType="datetime"
						format="shortDateShortTime"
						defaultSortOrder="desc"
						filterOperations={DATE_FILTER_OPERATIONS}
						allowHeaderFiltering={false}
						hidingPriority={2}
					/>
				</DataGrid>
			</div>

			<Snackbar
				message={snackbarOptions.message}
				onHiding={() =>
					setSnackbarOptions((current) => ({
						...current,
						visible: false,
					}))
				}
				of={".archived-emails-page"}
				options={snackbarOptions}
			/>
		</div>
	);
};

export default ArchivedEmailsPage;
